import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  clearImageCacheStorage,
  getImageCacheConsentStatus,
  IMAGE_CACHE_PREFERENCES_QUERY_KEY,
  registerImageCacheServiceWorker,
  setImageCacheConsentStatus,
  unregisterImageCacheServiceWorkers,
  type ImageCacheConsentStatus,
} from "@/lib/imageCacheConsent";

const HomeCookieCacheBanner = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [consentStatus, setConsentStatus] = useState<ImageCacheConsentStatus>("unset");
  const manageMode = searchParams.get(IMAGE_CACHE_PREFERENCES_QUERY_KEY) === "1";

  useEffect(() => {
    setConsentStatus(getImageCacheConsentStatus());
  }, []);

  const onAccept = async () => {
    setImageCacheConsentStatus("accepted");
    setConsentStatus("accepted");
    await registerImageCacheServiceWorker();
    if (manageMode) {
      const next = new URLSearchParams(searchParams);
      next.delete(IMAGE_CACHE_PREFERENCES_QUERY_KEY);
      setSearchParams(next, { replace: true });
    }
  };

  const onDecline = async () => {
    setImageCacheConsentStatus("declined");
    setConsentStatus("declined");
    await unregisterImageCacheServiceWorkers();
    await clearImageCacheStorage();
    if (manageMode) {
      const next = new URLSearchParams(searchParams);
      next.delete(IMAGE_CACHE_PREFERENCES_QUERY_KEY);
      setSearchParams(next, { replace: true });
    }
  };

  const shouldShowBanner = consentStatus === "unset" || manageMode;

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <div id="cookie-preferences" className="fixed inset-x-0 bottom-20 z-[55] px-3 md:bottom-4 md:px-4">
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card/95 p-4 shadow-2xl backdrop-blur">
        <p className="text-sm font-semibold text-foreground">Cookies & browser cache</p>
        <p className="mt-1 text-xs text-muted-foreground">
          By continuing to use this site, cache and cookies will be stored on your browser to improve performance and remember your preferences.
        </p>
        {manageMode && consentStatus !== "unset" && (
          <p className="mt-1 text-xs text-muted-foreground">Current preference: <span className="font-semibold text-foreground">{consentStatus}</span></p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void onAccept()}>I Accept</Button>
          <Button size="sm" variant="outline" onClick={() => void onDecline()}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default HomeCookieCacheBanner;
